import { useEffect, useRef } from 'react';
import useRetry, { SetRetryFunction } from './retry';
import { getReplacementServiceStoreId, isStoreService } from '../helpers';
import analyticsApiClient from '../services/Analytics';
import { useStateDispatch, useStateSelector } from '../store';
import {
  updateLoadingState,
  updateSelectedReplacementServiceStore,
} from '../reducers/app/appReducer';
import { getTimeSlots } from '../reducers/timeslots/timeSlotsReducer';
import { NO_STORE_ID } from '../services/Appointment/model';
import { getStores } from '../reducers/stores/storesReducer';
import {
  selectAppOperation,
  selectSelectedOriginalService,
} from '../reducers/stores/selectors';

const MAX_TIME_SLOT_COUNT_TO_SHOW_REPLACEMENT = 5;

type GetAvailableTimeslotsFunction = (
  from: string,
  setShowRetry: SetRetryFunction,
) => Promise<void>;

export type GetAvailableTimeslotsArgs = [string];

export default function useGetAvailableTimeslots(
  isBusy: boolean,
): [boolean, (args?: GetAvailableTimeslotsArgs) => void] {
  const dispatch = useStateDispatch();
  const { handleEventInGoogleAnalytic } = analyticsApiClient();
  const storeIdRef = useRef<string>('');
  const operation = useStateSelector(selectAppOperation);
  const storeId = useStateSelector((state) => state.app.selected.storeId);
  const serviceCode = useStateSelector(
    (state) => state.app.selected.serviceCode,
  );
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const from = useStateSelector((state) => state.timeSlots.from);
  const zipCode = useStateSelector((state) => state.search.postCode);
  const originalService = useStateSelector(selectSelectedOriginalService);
  const replacementServiceStores = useStateSelector(
    (state) => state.stores.replacementServiceStores,
  );

  const { location = '', replacementService } = originalService || {};
  const {
    location: replacementServiceLocation = '',
    serviceId: replacementServiceId = '',
    serviceCode: replacementServiceCode = '',
  } = replacementService || {};

  const setLatestStoreId = (requestId: string): void => {
    storeIdRef.current = requestId;
  };

  async function asyncGetAvailableTimeslots(
    fromParam: string,
    setShowRetry: SetRetryFunction,
  ): Promise<void> {
    if (!location || !storeId) return;

    try {
      setLatestStoreId(storeId);
      dispatch(updateLoadingState(true));

      const { availableTimeSlots: originalSlotsWithTimeSlots } = await dispatch(
        getTimeSlots({
          zipCode,
          serviceId,
          location,
          storeId,
          from: fromParam,
          serviceCode,
        }),
      ).unwrap();

      if (!originalSlotsWithTimeSlots) {
        throw new Error("Couldn't get timeslots");
      }

      if (!originalSlotsWithTimeSlots.length) {
        handleEventInGoogleAnalytic(
          `no_time_windows_${serviceId}_${operation}_appointment`,
        );
      }

      const totalOriginalTimeSlots = originalSlotsWithTimeSlots.reduce(
        (acc, slot) => {
          return acc + slot.timeSlots.length;
        },
        0,
      );

      if (
        (CSS_CONFIG.SERVICES.replacementServiceAlwaysShow ||
          totalOriginalTimeSlots < MAX_TIME_SLOT_COUNT_TO_SHOW_REPLACEMENT) &&
        replacementServiceId &&
        replacementServiceCode &&
        replacementServiceLocation
      ) {
        try {
          let replacementStores = [...replacementServiceStores];
          let replacementServiceStore = null;

          if (
            isStoreService(replacementServiceLocation) &&
            !replacementStores.length
          ) {
            const { stores: newReplacementStores } = await dispatch(
              getStores({
                zipCode,
                serviceProductId: replacementServiceId,
                isReplacementServiceStore: true,
              }),
            ).unwrap();
            replacementStores = [...newReplacementStores];
          }

          const replacementServiceStoreId = getReplacementServiceStoreId(
            storeId,
            replacementServiceId,
            replacementServiceLocation,
            replacementStores,
            CSS_CONFIG,
          );

          if (replacementServiceStoreId) {
            replacementServiceStore =
              replacementStores.find(
                (store) => store.value === replacementServiceStoreId,
              ) || null;

            dispatch(
              updateSelectedReplacementServiceStore({
                store: replacementServiceStore,
                storeId: replacementServiceStoreId,
              }),
            );

            await dispatch(
              getTimeSlots({
                zipCode,
                serviceId: replacementServiceId,
                location: replacementServiceLocation,
                storeId: replacementServiceStoreId,
                from: fromParam,
                serviceCode: replacementServiceCode,
                isReplacementService: true,
              }),
            );
          }
        } catch (replacementTimeSlotError) {
          // fail fetching replacement time slots silently
        }
      }
    } catch (originalTimeSlotError) {
      await setShowRetry(true);
    } finally {
      dispatch(updateLoadingState(false));
    }
  }

  const [showRetry, onRetry, setShowRetry] = useRetry<
    GetAvailableTimeslotsFunction,
    GetAvailableTimeslotsArgs
  >(asyncGetAvailableTimeslots, [from]);

  useEffect((): void => {
    if (!location || !storeId || isBusy) return;

    if (
      !isStoreService(location) ||
      (isStoreService(location) && storeId !== NO_STORE_ID)
    ) {
      setShowRetry(false);
      asyncGetAvailableTimeslots(from, setShowRetry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, storeId, isBusy]);

  return [showRetry, onRetry];
}
